import type { ComponentProps, ReactNode } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '../../lib/utils';

/**
 * A tooltip that can hold more than one line.
 *
 * The native `title` attribute was doing this job and is not adequate for it: the browser decides
 * when it appears, it cannot be styled, it collapses newlines on some platforms, and on a touch
 * device it never shows at all. A status icon whose only explanation is a `title` is an icon with
 * no explanation on half the devices people use.
 */
export function TooltipProvider({ children }: { children: ReactNode }) {
  // 150ms: long enough that sweeping the pointer across a table does not flash a trail of
  // tooltips, short enough that deliberately resting on one feels immediate.
  return <TooltipPrimitive.Provider delayDuration={150}>{children}</TooltipPrimitive.Provider>;
}

export function Tooltip({
  content,
  children,
  side = 'top',
  className,
}: {
  content: ReactNode;
  children: ReactNode;
  side?: ComponentProps<typeof TooltipPrimitive.Content>['side'];
  className?: string;
}) {
  return (
    <TooltipPrimitive.Root>
      {/* asChild so the trigger is the caller's own element rather than a wrapper button, which
          would otherwise land in the tab order and read as interactive to a screen reader. */}
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={6}
          collisionPadding={8}
          className={cn(
            'z-50 max-w-xs rounded-md border border-border bg-popover px-2.5 py-1.5',
            'text-xs leading-relaxed text-popover-foreground shadow-md',
            'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
            className,
          )}
        >
          {content}
          <TooltipPrimitive.Arrow className="fill-popover" width={10} height={5} />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}
